import InteractionData from '@nykaa/listing-shared/type/interactionData';
import {
  BestSellerTagInfoCategory,
  TagObject,
} from '@nykaa/product-card/types';
import { InlineFilter, AppliedFilter } from '@nykaa/inline-filter/types';
import { InlineWidgetConfig, InlineWidgets } from '@nykaa/listing-shared/type';
import { PriceRevealInfo } from '@nykaa/product-card-shared/types';
import {
  PrimaryCategories,
  CallOutTemplate,
  CohortSaleTemplate,
  SocialProofNudge,
  EarlyAccessData,
} from 'store/product/types';
import { BreadCrumb } from 'store/app/types';
import { UrgencyTemplate } from '@nykaa/ui-kit/component/PriceFluctuationNudge/types';
import { ListingGuides } from '@nykaa/listing-shared/type/filter';
import { CHIP_TYPE } from '@nykaa/inline-filter/components/MultiFilterWidget/types';
import { InlineGuidesV2 as InlineMultiWidget } from '@nykaa/inline-filter/components/MultiFilterWidget/types';
import { EddInfo } from '@nykaa/product-card/types';

export interface MatchProps {
  slug: string;
  id: string;
}

export interface NewTags {
  bgColor: string;
  borderColor: string;
  fontSize: string;
  fontWeight: string | number;
  title: string;
  titleColor: string;
  opacity: number;
}

export interface Facet {
  count: string;
  id: string;
  image_url: string;
  name: string;
}
export interface VisualFilter {
  facet_doc_count: number;
  facets: Facet[];
  key: string;
  show_search_box: boolean;
  title: string;
  tracking_group: string;
  type: string;
}

// TODO: MSR-1499 - Move this to be reused by Search Listing
export interface ListingProduct {
  brandIds: string[];
  brandName: string;
  buttonText: string;
  newButtonText?: string;
  categoryIds: string[];
  // ! FIXME: MSR-1499 this seems totally unnecessary
  childId: string;
  discount: number;
  dynamicTags: string[] | TagObject[];
  id: string;
  imageUrl: string;
  inStock: boolean;
  mrp: number;
  name: string;
  offersCount: number;
  onlyWishlistButton: boolean;
  parentId: string;
  price: number;
  offerPrice: number;
  featured: boolean;
  primaryCategories: PrimaryCategories;
  productId: string;
  proFlag: number;
  quantity: number;
  rating: number;
  ratingCount: number;
  slug: string;
  type: string;
  title: string;
  productTitle: string;
  variantCount: number;
  variantType: string;
  isBackorder: boolean;
  isLux: number;
  sku?: string;
  newTags: NewTags[];
  offer: string;
  offerColor: string;
  showOffer: boolean;
  offerId: string;
  pageSource?: string;
  contentLang?: {
    name: string;
  };
  trackingParams?: string;
  packSize: string;
  isMini?: boolean;
  tracking_metadata: any; // because it can be object/string/any, it will be send by the backend
  prData?: PriceRevealInfo | null;
  isPrEnabled?: boolean;
  isEarlyAccess?: boolean;
  isFlashSale?: boolean;
  callOutsTemplate: CallOutTemplate;
  cohortSaleTemplate?: CohortSaleTemplate | null;
  cohortSaleTemplateV2?: CohortSaleTemplate | null;
  urgencyTemplate?: UrgencyTemplate | null;
  social_proof_nudges?: SocialProofNudge | null;
  bestSellerTagInfo?: BestSellerTagInfoCategory;
  isFreeSample: boolean;
  isBestPriceLoaded?: boolean;
  left_tags?: string[];
  curation_tags?: string[];
  secondary_tags?: string[];
  bestPrice?: string;
  siblingColourCodes?: string[];
  disablePriveProduct?: boolean;
  earlyAccessData: EarlyAccessData;
  eddInfo?: EddInfo | null;
  plpPdpBridge?: PlpPdpBridge;
  leftTagText: string;
  secondaryTagV2: string;
  productNudges: {
    attribute_nudges: Record<string, string[]>;
    non_attribute_nudges: string[];
  };
}

export interface ListingData {
  categoryName: string;
  metaDescription?: string;
  metaKeywords?: string;
  canonical?: string;
  canonicalUrl?: string;
  metaTitle?: string;
  products: ListingProduct[];
  sort: string;
  totalProducts: number;
  filters: any;
  stopFurtherCall: boolean;
  guides: ListingGuides[];
  guidesV2: ListingGuides[];
  newUi?: boolean;
  ctaVisible?: boolean;
  reviewRatingVisible?: boolean;
  imageAspectRatio?: number;
  isApiFailed?: boolean;
  trackingMetadata?: Record<string, any>;
  totalFound?: string | number;
  inlineFilters: Record<number, InlineFilter>;
  url?: string;
  brandName?: string;
  listingType?: string;
  inlineWidgetConfig?: InlineWidgetConfig;
  title?: string;
  inlineFiltersV2: InlineMultiWidget | null;
  hasEdd?: boolean;
  visualFilters?: VisualFilter[];
}

export interface ActiveFilters {
  [key: string]:
    | string
    | number
    | {
        title: string;
        type: string;
        data: Record<string, string>;
      };
}

export interface CategoryListState {
  errorFetchingPage: boolean;
  isFetchingError: boolean;
  isNotFound: boolean;
  listingData?: ListingData;
  isProductListLoaded: boolean;
  loading: boolean;
  pageNumber: number;
  activeFilters: ActiveFilters;
  isFilterChanged: boolean;
  activeSort: string;
  stopFurtherCall: boolean;
  widgets: any[];
  isPageChanged: boolean;
  serverError: boolean;
  loadingFilterChange: boolean;
  hasInteracted: boolean;
  lang?: string;
  changeFrom?: string;
  isClearAll?: boolean;
  appliedFilters: AppliedFilter;
  isFilterOpen?: boolean;
  activeFilterType?: string;
  remaining_inventories_ids?: string[];
  queryParamsObject?: object;
  fetchedCategoyId?: string;
  inlineWidgets: InlineWidgets;
  breadcrumbs: BreadCrumb[];
  interactionData: InteractionData;
  isBestPriceLoading: boolean;
  bestPriceGradient: string[];
  bestPriceColor: string;
  bestOfferColor: string;
  eddFilterData: any;
  selectedVisualFilters: {
    key?: string;
    id?: string;
    name?: string;
  };
  fetchFooterDataOnClient: boolean;
}

export interface FetchCategoryListingParams {
  categoryId: string;
  pageNumber: number;
  isPro: boolean;
  sort?: string;
  changeFrom?: string;
  [key: string]: any;
  isPLP: boolean;
  pincode?: string;
  // TODO: MSR-1499 - Add more elements for filters and sort
}

export interface FetchEddFilterListParams {
  categoryId: string;
  pageNumber: number;
  isPro: boolean;
  sort?: string;
  changeFrom?: string;
  [key: string]: any;
  isPLP: boolean;
  pincode?: string;
}

export interface TipTile {
  url: string;
  imageSrc: string;
}

export enum ListingItemObjectType {
  PRODUCT = 'product',
  TIP_TILE = 'tiptile',
}
/**
 * reference: https://www.typescriptlang.org/docs/handbook/advanced-types.html#using-type-predicates
 */
export const isListingItemTipTile = (
  listingItem: ListingProduct | TipTile
): listingItem is TipTile => (listingItem as TipTile).imageSrc !== undefined;

export interface PageLocationType {
  pageType?: string;
  listingPageType?: string;
  id?: string;
  actionSource?: string;
}

export enum OfferListingRoot {
  PDP = 'pdp',
  CART = 'cart',
}

export enum OfferRootValue {
  PRODUCT_DETAIL_VIEW_OFFER = 'ProductDetailViewOffer',
  CART_VIEW_OFFER = 'CartViewOffer',
}

export interface PriceReveal {
  status: boolean;
  categoryId?: string[];
  text: string;
  textColor: string;
  badgeColor: string;
}

export interface Filter {
  name: string;
  id: string;
  score: string;
}

export interface InlineFilter {
  type: string;
  name: string;
  title: string;
  bg_color: string;
  is_multi_select: boolean;
  redirect_filter: string;
  redirect_text: string;
  position: number;
  values: Filter[];
}
export interface InlineFiltersData {
  [key: string]: InlineFilter;
}

export interface BgColor {
  color: string;
  transparency: number;
}

export interface EddInfoData {
  tag: string;
  name: string;
  text_color: string;
  bg_color: {
    color: string;
    transparency: number;
  }[];
}

export interface BestSellerPageState {
  bestSellerData: ListingData;
  interactionData: InteractionData;
  loading: false;
  serverError: false;
  isFetchingError: false;
  pageNumber: 0;
}

export interface PlpPdpBridgeImage {
  type: string;
  url: string;
}

export interface PlpPdpBridge {
  images: PlpPdpBridgeImage[];
}

export interface InlineFilterValue {
  name: string;
  id: string;
  score?: string;
  image_url?: string;
  color_code?: string;
}

export interface InlineFiltersV2 {
  type: string;
  name: string;
  title: string;
  is_multi_select: boolean;
  bg_color: string;
  redirect_filter: string;
  redirect_text: string;
  option_icon_type: CHIP_TYPE;
  values?: InlineFilterValue[];
  position?: number;
}

export interface InlineGuidesV2 {
  positions: string[];
  parent_title: string;
  background_color_url: string;
  filters: InlineFiltersV2[];
}
